import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

export interface UrlValidatorOptions {
  requireProtocol?: boolean;
}

/**
 * Validator function that checks if the input is a valid URL or domain name.
 * Can validate domain-only format or require full URL with protocol.
 *
 * @param options Configuration options for the validator.
 * @param options.requireProtocol If true, requires http:// or https:// protocol.
 * @returns ValidatorFn that returns null if valid, or ValidationErrors if invalid.
 */
export const urlValidator = (options?: UrlValidatorOptions): ValidatorFn => {
  return (control: AbstractControl): ValidationErrors | null => {
    const value = control.value;

    // Allow empty values (for optional fields)
    if (!value || value.trim() === '') {
      return null;
    }

    const input = value.trim();
    const requireProtocol = options?.requireProtocol ?? false;

    // If protocol is required, validate full URL
    if (requireProtocol) {
      return validateFullUrl(input);
    }

    // Default behavior: validate domain only
    return validateDomain(input);
  };
};

/**
 * Validates a full URL with required protocol.
 *
 * @param url The URL string to validate.
 * @returns ValidationErrors or null.
 */
const validateFullUrl = (url: string): ValidationErrors | null => {
  // Check for required protocol
  if (!url.includes('://')) {
    return { urlError: 'URL must include http:// or https:// protocol' };
  }

  // Check for valid protocol (only http and https allowed)
  if (!url.startsWith('http://') && !url.startsWith('https://')) {
    return { urlError: 'URL must use http:// or https:// protocol' };
  }

  // Extract domain part after protocol
  const protocolMatch = url.match(/^https?:\/\/(.+)/);

  if (!protocolMatch) {
    return { urlError: 'Invalid URL format' };
  }

  let domainPart = protocolMatch[1];

  // Remove path, query, and fragment parts for domain validation
  domainPart = domainPart.split('/')[0].split('?')[0].split('#')[0];

  // Handle port numbers
  const portMatch = domainPart.match(/^(.+):(\d+)$/);

  if (portMatch) {
    const port = parseInt(portMatch[2], 10);

    if (port < 1 || port > 65535) {
      return { urlError: 'Invalid port number' };
    }

    domainPart = portMatch[1];
  }

  // Validate the domain part
  return validateDomainPart(domainPart);
};

/**
 * Validates a domain-only input.
 *
 * @param domain The domain string to validate.
 * @returns ValidationErrors or null.
 */
const validateDomain = (domain: string): ValidationErrors | null => {
  // Check for invalid characters (spaces, protocols, paths, etc.)
  if (domain.includes(' ') ||
      domain.includes('://') ||
      domain.includes('/') ||
      domain.includes('?') ||
      domain.includes('#')) {
    return { urlError: 'Invalid domain format' };
  }

  return validateDomainPart(domain);
};

/**
 * Validates the domain part of a URL or domain-only input.
 *
 * @param domain The domain string to validate.
 * @returns ValidationErrors or null.
 */
const validateDomainPart = (domain: string): ValidationErrors | null => {
  // Special case for localhost
  if (domain === 'localhost') {
    return null;
  }

  // Check for consecutive dots or leading/trailing dots
  if (domain.includes('..') ||
      domain.startsWith('.') ||
      domain.endsWith('.')) {
    return { urlError: 'Invalid domain format' };
  }

  // Must contain at least one dot (TLD required)
  if (!domain.includes('.')) {
    return { urlError: 'Invalid domain format' };
  }

  // Basic domain regex pattern
  // Allows: letters, numbers, hyphens, dots
  const domainPattern = /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)*[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?$/;

  // Test against the regex pattern
  if (!domainPattern.test(domain)) {
    return { urlError: 'Invalid domain format' };
  }

  // Additional validation: each part should not exceed 63 characters
  const parts = domain.split('.');

  for (const part of parts) {
    if (part.length === 0 || part.length > 63) {
      return { urlError: 'Invalid domain format' };
    }

    // Parts cannot start or end with hyphen
    if (part.startsWith('-') || part.endsWith('-')) {
      return { urlError: 'Invalid domain format' };
    }
  }

  return null;
};
